<template>
  <div class="customer-signin-page-bg">
    <header class="signin-header">
      <div class="header-content">
        <div class="logo-container">
          <img src="@/assets/logo.png" alt="Tartoro Logo" class="header-logo" style="width: 54px; height: 54px;" />
          <span class="header-title" style="color: #000000; font-size: 24px; font-weight: 600;">Tartoro</span>
        </div>
        <div class="language-dropdown" @click="toggleDropdown">
          <span class="globe-icon">🌐</span>
          <span id="selected-language">{{ currentLanguage }}</span>
          <span class="chevron-down">▼</span>
          <div class="dropdown-content" v-show="isDropdownOpen">
            <a href="#" @click.prevent="changeLanguage('en')">English</a>
            <a href="#" @click.prevent="changeLanguage('es')">Español</a>
          </div>
        </div>
      </div>
    </header>
    <div class="customer-signin-container">
      <!-- Left Side: Welcome Section -->
      <div class="signin-left">
        <div class="signin-overlay"></div>
      </div>
      <!-- Right Side: OTP Form -->
      <div class="signin-right">
        <div class="signin-box">
          <div class="back-button-container">
            <BackButton @back="$router.push('/customer-signin')" />
          </div>
          <div class="otp-logo-title">
            <img src="@/assets/logo.png" alt="Tartoro Logo" class="otp-logo" />
            <span class="otp-title">Tartoro</span>
          </div>
          <h1 style="font-size:2rem; font-weight:700; color:#222; margin-bottom:0.7rem; margin-top: 2rem;">Verify your identity</h1>
          <p style="color:#555; font-size:1.08rem; margin-bottom:1.5rem;">Enter the 4-digit code sent to your email to continue. This helps us keep your account secure.</p>
          <form @submit.prevent="handleVerify" class="otp-form">
            <div class="otp-form-group">
              <label for="otp">{{ $t('otpVerification.form.label') }}</label>
              <div class="otp-inputs">
                <input v-for="(digit, idx) in otp" :key="idx" type="text" maxlength="1" class="otp-input" v-model="otp[idx]" @input="onInput(idx, $event)" @keydown.backspace="onBackspace(idx)" :ref="`otp${idx}`" />
              </div>
            </div>
            <div v-if="error" class="otp-error-message">{{ error }}</div>
            <button type="submit" class="otp-button" :disabled="loading || !isOtpComplete">
              {{ loading ? $t('otpVerification.form.verifying') : $t('otpVerification.form.verify') }}
            </button>
            <div class="otp-resend">
              {{ $t('otpVerification.form.resend') }}
              <a href="#" @click.prevent="handleResend" :style="{pointerEvents: countdown > 0 ? 'none' : 'auto', opacity: countdown > 0 ? 0.5 : 1}">
                {{ resendLoading ? $t('otpVerification.form.resending') : (countdown > 0 ? countdown : $t('otpVerification.form.resendLink')) }}
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@/styles/OtpVerification.css';
import '@/styles/CustomerSignIn.css';
import authService from '@/services/authService';
import BackButton from '@/components/BackButton.vue';

export default {
  name: 'CustomerOtpVerification',
  components: {
    BackButton
  },
  data() {
    return {
      otp: ['', '', '', ''],
      error: '',
      loading: false,
      resendLoading: false,
      email: '',
      countdown: 0,
      isDropdownOpen: false,
      currentLanguage: 'English'
    }
  },
  computed: {
    isOtpComplete() {
      return this.otp.every(d => d && d.length === 1 && /[0-9]/.test(d));
    }
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    changeLanguage(lang) {
      this.currentLanguage = lang === 'en' ? 'English' : 'Español';
      this.$i18n.locale = lang;
      localStorage.setItem('language', lang);
      this.isDropdownOpen = false;
    },
    onInput(idx, event) {
      const value = event.target.value.replace(/[^0-9]/g, '');
      this.otp[idx] = value;
      if (value && idx < 3) {
        this.$nextTick(() => {
          this.$refs[`otp${idx+1}`][0].focus();
        });
      }
    },
    onBackspace(idx) {
      if (!this.otp[idx] && idx > 0) {
        this.$refs[`otp${idx-1}`][0].focus();
      }
    },
    async handleVerify() {
      this.error = '';
      if (!this.isOtpComplete) {
        this.error = this.$t('otpVerification.errors.incomplete');
        return;
      }
      this.loading = true;
      try {
        const response = await authService.verifyOTP(this.email, this.otp.join(''));
        if (response.success) {
          localStorage.setItem('customerToken', response.token);
          localStorage.setItem('customerData', JSON.stringify(response.customer));
          localStorage.setItem('customerId', response.customer.id);
          
          // Load businesses after successful verification
          try {
            const businessesResponse = await authService.getCustomerBusinesses(response.customer.id);
            if (businessesResponse && businessesResponse.success && businessesResponse.businesses) {
              localStorage.setItem('userBusinesses', JSON.stringify(businessesResponse.businesses));
            }
          } catch (businessError) {
            console.error('Error loading businesses:', businessError);
          }
          
          this.$router.push({ 
            path: '/dashboard',
            query: { customerId: response.customer.id }
          });
        } else {
          throw new Error(response.message || this.$t('otpVerification.errors.verificationFailed'));
        }
      } catch (error) {
        this.error = error.message || this.$t('otpVerification.errors.verificationFailed');
      } finally {
        this.loading = false;
      }
    },
    async handleResend() {
      if (this.countdown > 0) return;
      this.error = '';
      this.resendLoading = true;
      try {
        const response = await authService.resendOTP(this.email);
        if (response.success) {
          this.startCountdown();
        } else {
          throw new Error(response.message || this.$t('otpVerification.errors.resendFailed'));
        }
      } catch (error) {
        this.error = error.message || this.$t('otpVerification.errors.resendFailed');
      } finally {
        this.resendLoading = false;
      }
    },
    startCountdown() {
      this.countdown = 60;
      const timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    }
  },
  created() {
    this.email = localStorage.getItem('customerEmail');
    if (!this.email) {
      this.$router.push('/customer-signin');
    }
    const savedLanguage = localStorage.getItem('language') || 'en';
    this.currentLanguage = savedLanguage === 'en' ? 'English' : 'Español';
    this.$i18n.locale = savedLanguage;
  }
}
</script>

<style scoped>
.back-button-container {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0;
  margin-top: 0;
  position: absolute;
  top: -1rem;
  left: 1rem;
}

.signin-box {
  position: relative;
  padding-top: 2rem;
}

.otp-logo-title {
  display: none;
}

.otp-logo {
  display: none;
}

.otp-title {
  display: none;
}
</style> 